<template>
  <div>
    <nav-bar />
    <div class="dashboard">
      <div class="dashboard__tabsWrapper">
        <div v-if="isDataLoaded">
          <WidgetsEdit :widgets="widgets" />
          <ComputedPropEdit :props="widgetsComputed" />
          <CustomCodeEdit :codeBase="customCode" />
          <div class="draggableContainer">
            <keep-alive>
              <CustomCodeSearch
                v-for="(code, key) in customCode"
                :key="key"
                v-if="codeSearchShown[key]"
                :type="key"
                :code="code"
                />
            </keep-alive>
          </div>

          <ContextMenu
            v-if="$can('update', 'Dashboard_' + currentDash)"
            :visible="isContextMenuVisible"
            :positionX="contextMenuPos.x"
            :positionY="contextMenuPos.y"
            @closeContextMenu="isContextMenuVisible = false"
            >
            <div>
              <b-list-group>
                <b-list-group-item
                  href="#"
                  @click.prevent="setTabOnLoad"
                  :class="{ contextMenu__toggle: tabOnLoad === currentTab }"
                  >
                  Select on load
                  <b-badge
                    v-if="tabOnLoad === currentTab"
                    variant="primary"
                    pill>
                    <icon name="check" />
                  </b-badge>
                </b-list-group-item>
                <b-list-group-item
                  href="#"
                  @click.prevent="setPrivateTab"
                  :class="{ contextMenu__toggle: ~privateTabs.indexOf(currentTab) }"
                  >
                  Private
                  <b-badge
                    v-if="~privateTabs.indexOf(currentTab)"
                    variant="primary"
                    pill>
                    <icon name="check" />
                  </b-badge>
                </b-list-group-item>
                <b-list-group-item
                  href="#"
                  @click.prevent="startRename"
                  >
                  Rename
                </b-list-group-item>
                <b-list-group-item
                  v-if="$count(tabs) > 1"
                  href="#"
                  @click.prevent="deleteTab"
                  >
                  Delete
                </b-list-group-item>
              </b-list-group>
            </div>
          </ContextMenu>

          <b-tabs
            v-if="tabs"
            no-key-nav
            v-model="tabIndex"
            class="dashboard__tabs"
            >

            <div v-if="$count(customCode.html)">
              <DynamicRender
                v-for="(html, idx) in customCode.html"
                :key="idx"
                v-if="!html.scope || html.scope === currentTab"
                :template="html.code"
                :data="widgetsData"
                :computed="widgetsComputedReady"
                />
            </div>

            <b-tab
              v-for="(tab, tabKey) in tabs"
              :key="tabKey"
              v-if="!~privateTabs.indexOf(tabKey) || $can('update', 'Dashboard_' + currentDash) || $can('manageUsers', 'Dashboard_' + currentDash)"
              @click="setCurrentTab(tabKey)"
              >
              <template
                slot="title"
                @click.prevent.stop
                >
                <span class="dashboard__tabTrigger">
                  <icon
                    name="low-vision"
                    class="dashboard__tabInvisible"
                    v-if="~privateTabs.indexOf(tabKey)"
                    v-b-tooltip.hover
                    title="Private tab"
                    />
                  <span
                    :data-tab="tabKey"
                    >{{ tab.title }}</span>
                  <span
                    v-if="$can('update', 'Dashboard_' + currentDash)"
                    class="dashboard__editTab"
                    @click="showContextMenu($event)"
                    >
                    <icon name="caret-down" />
                  </span>
                </span>
              </template>
              <Grid
                :widgets="widgets"
                :widgetsData="widgetsData"
                :widgetsComputed="widgetsComputedReady"
                :currentTab="tabKey"
                />
            </b-tab>
            <b-nav-item
              v-if="$can('update', 'Dashboard_' + currentDash)"
              slot="tabs"
              class="dashboard__newTab dashboard__tabTrigger"
              @click="addNewTab"
              >
              <icon name="plus" />
            </b-nav-item>
          </b-tabs>
        </div>
      </div>
      <Toolbar />
    </div>
    <CustomCodeRender
      v-if="markupMounted"
      :customCode="customCode"
      :scope="currentTab"
      :widgetsData="widgetsData"
      :widgetsComputed="widgetsComputedReady"
      />
  </div>
</template>

<script>
// components
import Grid from './Grid'
import Toolbar from '@/components/Toolbar'
import NavBar from '@/components/NavBar'
import ContextMenu from '@/components/ContextMenu'
import WidgetsEdit from '@/components/WidgetsEdit'
import ComputedPropEdit from '@/components/ComputedPropEdit'
import CustomCodeEdit from '@/components/CustomCodeEdit'
import CustomCodeRender from '@/components/CustomCodeRender'
import DynamicRender from '@/components/DynamicRender'
import CustomCodeSearch from '@/components/CustomCodeSearch'
import Pivot from '@marketconnect/vue-pivot-table'
import PivotTable from '@marketconnect/vue-pivot-table'
// modules
import { mapState } from 'vuex'
import FormulaCaching from '@/components/Spreadsheet/FormulaCaching'
// services
import db from '@/services/db'
// helpers
import utils from '@/helpers/utils'

import eventBus from '@/helpers/event-bus'



let $ = window.$

export default {
  components: {
    Grid,
    Toolbar,
    NavBar,
    ContextMenu,
    WidgetsEdit,
    ComputedPropEdit,
    CustomCodeEdit,
    CustomCodeRender,
    DynamicRender,
    CustomCodeSearch,
    Pivot, 
    PivotTable
  },
  data () {
    return {
      isPublic: false,
      tabs: null,
      widgets: null,
      widgetsData: null,
      widgetsComputed: null,
      customCode: null,
      isDataLoaded: false,
      isContextMenuVisible: false,
      contextMenuPos: {
        x: 0,
        y: 0
      },
      renameStarted: false,
      lastKnownTitle: '',
      markupMounted: false,
      tabIndex: 0,
      tabOnLoad: null,
      tabOnLoadSelected: false,
      tabRenameStarted: false,
      tabToRename: '',
      codeSearchShown: {
        js: false,
        css: false,
        html: false
      },
      setTabContentHeightIntId: null,
      privateTabs: []
    }
  },
  watch: {
    isDataLoaded () {
      setTimeout(() => {
        this.markupMounted = true
        this.$store.commit('setDashboardLoadingStatus', false)
      }, 500)
    }
  },
  computed: {
    ...mapState([
      'currentTab',
      'currentDash',
      'userId',
      '$profile',
      'isNFLApp',
      'dashboardModels'
    ]),
    widgetsComputedReady () {
      let computed = {}

      for (let key in this.widgetsComputed) {
        if (!this.widgetsComputed[key]) continue
        computed[key] = new Function(this.widgetsComputed[key]) // eslint-disable-line no-new-func
      }

      return computed
    }
  },
  methods: {
    setCurrentTab (activeTab) {
      this.isContextMenuVisible = false
      this.$nextTick(() => {
        if (activeTab) {
          this.setRequiredTab(activeTab)
        } else {
          this.setInitialTab()
        }
      })
    },
    setRequiredTab (tab, queryFirst) {
      let curTab
      let vm = this
      if (queryFirst) curTab = this.$route.query.tab
      if (!curTab) curTab = tab

      this.$router.push({
        name: 'Dashboard',
        params: {
          id: vm.currentDash
        },
        query: {
          tab: curTab
        }
      })

      let curTabs = Object.keys(this.tabs)
      let tabIdx = curTabs.indexOf(curTab)
      if (tabIdx !== undefined) this.tabIndex = tabIdx
      this.$store.commit('setCurrentTab', curTab)
    },
    setInitialTab () {
      let tabInUrl = this.$route.query.tab

      if (tabInUrl && this.tabs[tabInUrl]) {
        this.setRequiredTab(tabInUrl)
      } else {
        if (this.tabOnLoad && this.tabs[this.tabOnLoad]) this.setRequiredTab(this.tabOnLoad)
        else this.setRequiredTab(Object.keys(this.tabs)[0])
      }
    },
    cacheDashboardSheets () {
      if (!this.widgetsData || !this.widgetsComputed) return
      let scope = {
        ...this.widgetsData,
        ...this.widgetsComputed
      }
      let scopedData = [
        ...Object.keys(this.widgetsData),
        ...Object.keys(this.widgetsComputed)
      ]
      scopedData.splice(scopedData.indexOf('spreadsheets'), 1)
      scope.scopedData = scopedData

      this.$store.commit('setDashboardSheetsCache', new FormulaCaching(scope))
    },
    rerenderCustomCode () {
      if (this.markupMounted) {
        this.markupMounted = false
        this.$nextTick(() => {
          this.markupMounted = true
        })
      }
    },
    addNewTab () {
      db.tabs.create(this.currentDash, this.$count(this.tabs), (err, newTabKey) => {
        if (err) {
          this.$store.commit('AddError', err.message)
        } else {
          this.$nextTick(() => {
            this.setCurrentTab(newTabKey)
          })
        }
      })
    },
    showContextMenu (event) {
      this.isContextMenuVisible = true
      this.contextMenuPos.x = event.clientX
      this.contextMenuPos.y = event.clientY
    },
    deleteTab () {
      let confirmDel = confirm('Are you sure?')

      if (!confirmDel) return
      this.isContextMenuVisible = false

      db.tabs.delete(this.currentTab, (err) => {
        if (err) {
          this.$store.commit('AddError', err.message)
        } else {
          this.tabIndex = 0
        }
      })
    },
    startRename () {
      let editable = $(`span[data-tab="${this.currentTab}"]`)
      this.isContextMenuVisible = false
      this.lastKnownTitle = editable.text()
      editable.attr('contenteditable', true)
      editable.focus()
      this.$cursorToEnd(editable.get(0))
      editable.on('keydown', (event) => {
        if (event.keyCode === 13) {
          event.preventDefault()
          editable.blur()
        }
      })
      editable.on('blur', () => {
        this.finishRename(editable)
      })
    },
    finishRename (editable) {
      editable.off('keydown')
      editable.off('blur')
      editable.attr('contenteditable', false)
      if (!editable.text()) {
        editable.text(this.lastKnownTitle)
        return
      }
      let newTitle = editable.text()
      if (newTitle === this.lastKnownTitle) return
      this.$store.commit('addProcess', {
        process: 'renameTab',
        message: 'Renaming'
      })
      db.tabs.rename(this.currentTab, newTitle, (err) => {
        this.$store.commit('deleteProcess', 'renameTab')
        if (err) this.$store.commit('addError', err.message)
      })
    },
    setTabOnLoad () {
      db.tabs.setTabOnLoad(this.currentTab)
      this.tabOnLoad = this.currentTab
      this.isContextMenuVisible = false
    },
    setTabContentHeight () {
      let tabs = $('.dashboard__tabs')
      let navs = tabs.find('> div > .nav-tabs')
      let content = tabs.find('> .tab-content')
      let heightToCut = navs.height() + $('.appNav').height() + 2

      content.height('calc(100vh - ' + heightToCut + 'px)')
    },
    setPrivateTab () {
      let privateTabs = []

      if (~this.privateTabs.indexOf(this.currentTab)) {
        privateTabs = this.privateTabs
          .filter((tab) => tab !== this.currentTab)
      } else {
        privateTabs = this.privateTabs.slice()
        privateTabs.push(this.currentTab)
      }

      db.tabs.setPrivateTab(privateTabs)
      this.isContextMenuVisible = false
    }
  },
  created () {
    let dashId = this.$route.params.id
    this.$store.commit('setCurrentDash', dashId)
    this.$store.commit('setDashboardLoadingStatus', true)

    new Promise((resolve) => {
      db.access.dashboard(this.userId, this.currentDash, (actions) => {
        if (!actions) {
          if (!this.isDataLoaded) resolve()
          return
        }
        let rule = this.$buildACLRule('Dashboard_' + this.currentDash, actions)
        this.$store.commit('updateCaslRules', rule)
        if (!this.isDataLoaded) resolve()
      })
    }).then(() => {
      console.log('get tab on load value');
      return new Promise((resolve) => {
        console.log('tab on load?');
        db.getRef('tabOnLoad').on('value', (snapshot) => {
          this.tabOnLoad = snapshot.val()
          if (!this.isDataLoaded) resolve()
        })
      })
    }).then(() => {
      return new Promise((resolve) => {
        db.getRef('privateTabs').on('value', (snapshot) => {
          this.privateTabs = snapshot.val() || []
          resolve()
        })
      })
    }).then(() => {
      return new Promise((resolve) => {
        db.tabs.read((tabs) => {
          this.tabs = tabs || {}
          this.setCurrentTab()
          if (!this.isDataLoaded) resolve()
        })
      })
    }).then(() => {
      return new Promise((resolve) => {
        db.dashboards.readOne(this.currentDash, (dashboard) => {
          if (this.userId === dashboard.owner) {
            this.$store.commit('setDashOwner', this.userId)
          }
          if (!this.isDataLoaded) resolve()
        })
      })
    }).then(() => {
      return new Promise((resolve) => {
        db.widgets.read((widgets) => {
          this.widgets = widgets || []
          if (!this.isDataLoaded) resolve()
        })
      })
    }).then(() => {
      return new Promise((resolve) => {
        db.widgets.readData((widgetsData) => {
          this.widgetsData = widgetsData || {}
          this.$store.commit('extractDashboardSheets', widgetsData)
          this.cacheDashboardSheets()
          if (!this.isDataLoaded) resolve()
        })
      })
    }).then(() => {
      return new Promise((resolve) => {
        db.widgets.readComputed((widgetsComputed) => {
          this.widgetsComputed = widgetsComputed || {}
          this.cacheDashboardSheets()
          if (!this.isDataLoaded) resolve()
        })
      })
    }).then(() => {
      return new Promise((resolve) => {
        db.customCode.read((customCode) => {
          this.customCode = customCode || {}
          if (!this.isDataLoaded) resolve()
        })
      })
    }).then(() => {
      return new Promise((resolve) => {
        db.dashboardModels.read((data) => {
          this.$store.commit('setDashboardModels', data)
          // set dahboard model methods
          window.$setDashboardModel = db.dashboardModels.setDashboardModel
          window.$getDashboardModel = utils.assignDashboardModelReader(this.dashboardModels)
          window.$addDashboardModel = db.dashboardModels.addDashboardModel
          if (!this.isDataLoaded) resolve()
        })
      })
    }).then(() => {
      if (this.isNFLApp) {
        return new Promise((resolve) => {
          console.log('get profile data');
          
          db.getProfilesInfo((info) => {
            if (this.$can('update', 'Dashboard_' + this.currentDash)) {
              this.$store.commit('setProfilesInfo', info || {})
            }
            if (!this.isDataLoaded) resolve()
          })
        })
      }
    }).then(() => {
      console.log('is this nfl app?');
      if (this.isNFLApp) {
        return new Promise((resolve) => {
          db.getProfileInfo(this.userId, (info) => {
            this.$store.commit('setProfileInfo', info || {})
            if (!this.isDataLoaded) resolve()
          })
        })
      }
    }).then(() => {
      if (this.isNFLApp) {
        return new Promise((resolve, reject) => {
          let id = this.$profile.websiteId
          db.getNFLPAUserData(id, (err, data) => {
            if (err || !data) {
              this.$router.push('/forbidden')
              console.log('is nfl app and is forbidden');
            } else {
              this.$store.commit('setNFLData', data)
              if (!this.isDataLoaded) resolve()
            }
          })
        })
      }
    }).then(() => {
      if (!this.isPublic) {
        return new Promise((resolve) => {
          let id = this.userId
          db.widgets.getUserModels(id, (data) => {
            this.$store.commit('setFBUserModels', data)
            if (!this.isDataLoaded) resolve()
          })
        })
      }
    }).then(() => {
      if (!this.isPublic) {
        return new Promise((resolve) => {
          db.getTrackModels((data) => {
            this.$store.commit('setFBTrackModels', data)
            if (!this.isDataLoaded) resolve()
          })
        })
      }
    }).then(() => {
      this.isDataLoaded = true

      this.rerenderCustomCode()
      this.setTabContentHeight()

      eventBus.$on('showSearchBar', (type) => {
        this.codeSearchShown[type] = true
      })
      eventBus.$on('hideSearchBar', (type) => {
        this.codeSearchShown[type] = false
      })
    }).catch((err) => {
      console.log('forbidden!')
      console.log(err)
      this.isDataLoaded = true
      this.$store.commit('setDashboardLoadingStatus', true)
    })
  },
  mounted () {
    this.$nextTick(() => {
      window.addEventListener('resize', this.setTabContentHeight)
    })

    this.setTabContentHeightIntId = setInterval(() => {
      this.setTabContentHeight()
    }, 200)
  },
  beforeDestroy () {
    this.tabs = null
    this.widgets = null
    this.widgetsData = null
    this.widgetsComputed = null
    this.customCode = null
    $('.customCssCode').remove()
    this.setTabContentHeightIntId = null
    window.$setDashboardModel = null
    window.$getDashboardModel = null
    window.$addDashboardModel = null
  }
}
</script>

<style lang="scss">
@import '~$scss/globals';

.dashboard {
  display: flex;
  &__tabsWrapper {
    width: 100%;
    height: calc(100vh - 60px);
    .tab-pane {
      padding: {
        top: 10px;
        bottom: 10px;
      }
    }
    .nav-link.active .dashboard__editTab {
      display: inline-block;
    }
    .tabs-component-panel {
      padding-bottom: 60px;
    }
  }
  &__tabs {
    > .tab-content {
      position: fixed;
      bottom: 0;
      left: 0;
      width: 100%;
      overflow: auto;
      padding-bottom: 40px;
      background-color: #f8f9fe;
      box-shadow: inset 1px 2px 2px rgba(0, 0, 0, .2);
      .tab-pane {
        outline: none;
      }
    }
  }
  &__tabTrigger {
    position: relative;
    z-index: 5;
  }
  &__newTab svg {
    vertical-align: -3px;
  }
  &__editTab {
    display: none;
    height: 100%;
    color: $color4;
    padding: 0 5px;
    margin-right: -5px;
    &:hover {
      color: $color5;
    }
    svg {
      vertical-align: -3px;
    }
  }
  &__tabInvisible {
    color: $color4;
    vertical-align: -3px;
    margin-right: 3px;
  }
}
.loading {
  padding: {
    left: 15px;
    top: 15px;
  }
}
.contextMenu__toggle {
  position: relative;
  padding-right: 45px !important;
  .badge {
    position: absolute;
    top: 9px;
    right: 8px;
    padding: {
      top: .10em;
      bottom: .10em;
    }
    vertical-align: 2px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/index.vue